// Role preview is a presentation filter over the signed-in session. Nothing
// here is sent to the server: it authorizes on the account's stored roles.

const COORDINATOR_ROLES = [
    'si_coordinator',
    'ta_coordinator',
    'la_coordinator',
];
const LOWER_ROLES = ['faculty', 'student'];

export const PREVIEW_ROLE_LABELS = Object.freeze({
    student: 'a student',
    faculty: 'a faculty member',
    si_coordinator: 'an SI coordinator',
    ta_coordinator: 'a TA coordinator',
    la_coordinator: 'an LA coordinator',
});

export const ROLE_NAMES = Object.freeze({
    student: 'Student',
    faculty: 'Faculty/staff',
    si_coordinator: 'SI coordinator',
    ta_coordinator: 'TA coordinator',
    la_coordinator: 'LA coordinator',
    admin: 'Admin',
});

const STORAGE_KEY = 'psh.previewRole';

/**
 * Roles the account may preview, downward only: an admin may preview any
 * coordinator role, faculty or student; a coordinator may preview faculty or
 * student. Everyone else gets none.
 *
 * @param {string[] | null | undefined} realRoles
 * @returns {string[]}
 */
export function getPreviewableRoles(realRoles) {
    const roles = realRoles || [];
    if (roles.includes('admin')) {
        return [...COORDINATOR_ROLES, ...LOWER_ROLES];
    }
    if (roles.some((role) => COORDINATOR_ROLES.includes(role))) {
        return [...LOWER_ROLES];
    }
    return [];
}

/**
 * The roles the interface renders from. Callers that read auth state from a
 * context the provider did not build fall back to the stored roles.
 */
export function resolveRoles({ effectiveRoles, userProfile }) {
    return effectiveRoles ?? userProfile?.roles ?? [];
}

/** Short label for the role in effect, shared by the user menu and banner. */
export function activeRoleName(effectiveRoles) {
    const roles = effectiveRoles || [];
    const priority = [
        'admin',
        'si_coordinator',
        'ta_coordinator',
        'la_coordinator',
        'faculty',
        'student',
    ];
    const first = priority.find((role) => roles.includes(role));
    return first ? ROLE_NAMES[first] : '';
}

export function readStoredPreview() {
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (
            parsed &&
            typeof parsed.uid === 'string' &&
            typeof parsed.role === 'string'
        ) {
            return { uid: parsed.uid, role: parsed.role };
        }
    } catch {
        // Unreadable or unavailable storage means no preview.
    }
    return null;
}

export function writeStoredPreview(preview) {
    try {
        if (preview) {
            window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(preview));
        } else {
            window.sessionStorage.removeItem(STORAGE_KEY);
        }
    } catch {
        // The preview still works for this page load without storage.
    }
}
